import { TFunction } from 'react-i18next';
import {
  InvalidLineError,
  InvalidDelimiterError,
  NoValidEmailColumnsFoundsError,
  ConsentFileData,
  ConsentFileAnalysedStatus,
  Column,
  ColumnType,
  ConsentFileType,
} from '../types';
import { makeRedStatus, makeGreenStatus, makeAmberStatus } from './ConsentFileStatusGenerator';

const DATE_REGEX = /[0-9]{4}[/-][0-9]{1,2}[/-][0-9]{1,2}|[0-9]{1,2}[/-][0-9]{1,2}[/-][0-9]{4}/;
const EMAIL_REGEX = /.+@.+\..+/;
const SHA256_HEX_REGEX = /^[a-fA-F0-9]{64}$/;

const isEmailOrSha256Hex = (value: string): boolean => EMAIL_REGEX.test(value) || SHA256_HEX_REGEX.test(value);
const SINGLE_FIELD_SEPARATOR_REGEX = /[|,;]+/g;
const COMBINED_FIELD_SEPARATOR_REGEX = /[\][_(){},;:+*!£#$%^&~?=€|/-]{2,}/g;
const TEXT_DELIMITER_REGEX = /[\\|'"]+/;
const TEXT_DELIMITER_LIMITS_REGEX = /^[\\|'"]+.*?[\\|'"]+$/;

export class ConsentFileDataParser {
  private lines: string[][] = [];
  private firstLine: string;
  private fieldSeparator: string;
  private textDelimiter: string;
  private columns: Column[] = [];
  private dateHeadingCount = 0;
  private emailHeadingCount = 0;

  private errors: Error[] = [];
  private status: ConsentFileAnalysedStatus;

  public constructor(
    private translator: TFunction<'fileManagement'>,
    private readonly fileName: string,
    private readonly consentType: ConsentFileType,
  ) {}

  public parse(fileLines: string[]): void {
    try {
      this.detectDelimiters(fileLines[0]);
      this.setLines(fileLines);
      this.setColumns();
      this.setStatus();
    } catch (err) {
      this.addError(err as Error);
    }
  }

  public addError(err: Error): void {
    this.errors.push(err);
    this.setStatus();
  }

  public data(): ConsentFileData {
    return {
      fileName: this.fileName,
      status: this.status,
    };
  }

  private detectDelimiters(firstLine: string): void {
    this.firstLine = firstLine ?? '';
    if (this.firstLine === '') {
      throw new InvalidLineError(this.translator('analysis.file.analysed.red.modal.error.type.blankFile'));
    }
    this.setTextDelimiter(); // important to check this first as it flushes text delimiters before field delimiter check
    this.setFieldSeparator();
  }

  private setTextDelimiter(): void {
    if (TEXT_DELIMITER_LIMITS_REGEX.test(this.firstLine)) {
      const uniqueDelimiters = new Set(this.firstLine.match(TEXT_DELIMITER_REGEX));

      if (uniqueDelimiters.size > 1) {
        throw new InvalidDelimiterError(
          this.translator('analysis.file.analysed.red.modal.error.type.multipleDelimiters'),
        );
      }

      const [textDelimiter] = [...uniqueDelimiters];
      this.textDelimiter = textDelimiter;
      // strip textDelimiter from first line before field delimiter checks
      this.firstLine = this.firstLine.replace(new RegExp(`[${textDelimiter.replace('\\', '\\\\')}]`, 'g'), '');
    }
  }

  private setFieldSeparator(): void {
    const combinedSeparators = new Set(this.firstLine.match(COMBINED_FIELD_SEPARATOR_REGEX));

    if (combinedSeparators.size > 1) {
      throw new InvalidDelimiterError(
        this.translator('analysis.file.analysed.red.modal.error.type.multipleDelimiters'),
      );
    }

    if (combinedSeparators.size === 1) {
      this.fieldSeparator = [...combinedSeparators][0];

      return;
    }

    const singleSeparators = new Set(this.firstLine.match(SINGLE_FIELD_SEPARATOR_REGEX));

    // if we have 0 unique delimiters we consider this to be valid (1 column) and allow
    // failure to confirm when checking for email content
    if (singleSeparators.size > 1) {
      throw new InvalidDelimiterError(
        this.translator('analysis.file.analysed.red.modal.error.type.multipleDelimiters'),
      );
    }

    this.fieldSeparator = [...singleSeparators][0];
  }

  private setLines(fileLines: string[]): void {
    if (this.textDelimiter) {
      const delimiterLength = this.textDelimiter.length;
      const splitPattern = `${this.textDelimiter}${this.fieldSeparator}${this.textDelimiter}`;
      this.lines = fileLines.map((line: string) => line.slice(delimiterLength, -delimiterLength).split(splitPattern));
    } else {
      this.lines = fileLines.map((line: string) => line.split(this.fieldSeparator));
    }
  }

  private setColumns(): void {
    const transposed = this.lines[0].map((_, colIndex) => this.lines.map(row => row[colIndex].trim()));

    this.columns = transposed
      .map((columnData: string[]) => {
        const columnType = this.columnType(columnData);
        let heading;

        // rules out others
        if (columnType === ColumnType.OTHER) {
          return null;
        }

        if (columnType === ColumnType.EMAIL) {
          heading = `${++this.emailHeadingCount}-${
            isEmailOrSha256Hex(columnData[0]) ? 'email-column' : columnData.shift()
          }`;
        } else {
          heading = `${++this.dateHeadingCount}-${DATE_REGEX.test(columnData[0]) ? 'date-column' : columnData.shift()}`;
        }

        const column: Column = {
          type: columnType,
          heading: heading,
          rows: columnData,
        };

        return column;
      })
      .filter(col => !!col);

    if (this.emailHeadingCount === 0) {
      throw new NoValidEmailColumnsFoundsError(
        this.translator('analysis.file.analysed.red.modal.error.type.noEmailDetected'),
      );
    }
  }

  private columnType(data: string[]): ColumnType {
    const target = Math.ceil(data.length * 0.1);
    const sampleData = data.slice(1, target * 2 + 1);
    // matches enough email addresses and/or SHA256 hex identifiers (may be mixed in one column)
    if (this.meetsTargetMatchesForPredicate(sampleData, target, isEmailOrSha256Hex)) {
      return ColumnType.EMAIL;
    }
    // matches enough dates
    if (this.meetsTargetMatchesForRegex(sampleData, target, DATE_REGEX)) {
      return ColumnType.DATE;
    }

    return ColumnType.OTHER;
  }

  private meetsTargetMatchesForRegex(data: string[], confidenceTarget: number, regex: RegExp): boolean {
    return this.meetsTargetMatchesForPredicate(data, confidenceTarget, entry => regex.test(entry));
  }

  private meetsTargetMatchesForPredicate(
    data: string[],
    confidenceTarget: number,
    matches: (entry: string) => boolean,
  ): boolean {
    let score = 0;

    return data.some((entry: string): boolean => {
      if (matches(entry)) {
        score++;
      }

      if (score === confidenceTarget) {
        return true;
      }

      return false;
    });
  }

  private setStatus(): void {
    if (this.errors.length > 0) {
      this.status = makeRedStatus(
        this.translator,
        this.errors.map(e => e.message),
        this.consentType,
      );
      return;
    }

    if (this.dateHeadingCount > 1 || this.emailHeadingCount > 1) {
      this.status = makeAmberStatus(this.translator, this.columns, this.consentType);
      return;
    }

    this.status = makeGreenStatus(this.translator, this.columns, this.consentType);
  }
}
